// Case study reads.
//
// Read-only access to closed decisions and the price tape that surrounds
// them. Nothing here writes engine state, and no engine thresholds or
// policy internals are exposed; only published levels and public bars.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import archiveRows from "@/data/case-study-archive.json";
import { caseStudyCardKey } from "@/lib/case-study-card";
import { swingTradedStop } from "@/lib/swing-traded-stop";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type CaseStudySummary = {
  id: string;
  displayId: string | null;
  asset: string;
  side: "Buy" | "Sell";
  result: string;
  pips: number | null;
  entry: number | null;
  sl: number | null;
  tp1: number | null;
  tp2: number | null;
  grade: string | null;
  source: string | null;
  timeframe: string | null;
  openedAt: string | null;
  closedAt: string;
  note: string | null;
};

export type CaseStudyCard = CaseStudySummary & { tape: number[]; bars: number; netR: number };

export type CaseStudyMonth = {
  key: string;
  label: string;
  trades: CaseStudyCard[];
  wins: number;
  losses: number;
  netR: number;
};

function riskR(t: CaseStudySummary): number {
  if (t.entry == null || t.sl == null) return 0;
  const risk = Math.abs(t.entry - t.sl);
  if (risk === 0) return 0;
  const usd = (t.pips ?? 0) / 100;
  return usd / risk;
}

// Archived rows store pips (1 pip = 0.10 USD). Resolve the net USD per oz
// with the same contract model the replay uses (half at TP1, half at TP2,
// stop to entry after TP1) so the header matches the replay stamp.
function legacyCents(r: Record<string, unknown>): number | null {
  const n = (v: unknown) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
  const entry = n(r.entry), sl = n(r.sl), tp1 = n(r.tp1), tp2 = n(r.tp2), pips = n(r.pips);
  const isBuy = (r.type as string) !== "Sell";
  const move = (p: number) => (isBuy ? p - entry! : entry! - p);
  const res = String(r.result ?? "");
  let usd: number | null = pips == null ? null : pips / 10;
  if (entry != null && sl != null) {
    if (/TP2/i.test(res) && tp1 != null && tp2 != null) usd = 0.5 * move(tp1) + 0.5 * move(tp2);
    else if (/TP1/i.test(res) && tp1 != null) usd = 0.5 * move(tp1);
    else if (/SL/i.test(res)) usd = -Math.abs(entry - sl);
  }
  return usd == null ? null : Math.round(usd * 100);
}

function toSummary(r: Record<string, unknown>): CaseStudySummary {
  const num = (v: unknown) => (v == null ? null : Number(v));
  return {
    id: String(r.id),
    displayId: (r.display_id as string) ?? null,
    asset: (r.asset as string) ?? "XAUUSD",
    side: (r.type as string) === "Sell" ? "Sell" : "Buy",
    result: (r.result as string) ?? "Closed",
    pips: legacyCents(r),
    entry: num(r.entry),
    sl: num(r.sl),
    tp1: num(r.tp1),
    tp2: num(r.tp2),
    grade: (r.quality_grade as string) ?? null,
    source: (r.source as string) ?? null,
    timeframe: (r.timeframe as string) ?? null,
    openedAt: (r.generated_at as string) ?? null,
    closedAt: String(r.created_at),
    note: (r.note as string) ?? null,
  };
}

const OTAS_PREFIX = "otas-";

function otasToSummary(r: Record<string, unknown>): CaseStudySummary | null {
  const st = (r.state ?? {}) as Record<string, unknown>;
  const n = (v: unknown) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
  const closed = r.closed_at as string | null;
  if (!closed) return null;
  const usd = n(st.grossUsdPerOriginalOz) ?? 0;
  const status = String(r.status ?? st.status ?? "CLOSED");
  const result =
    status === "CLOSED_SL" ? "Risk Invalidation" : status === "CLOSED_TP2" ? "Target Achieved" : usd > 0 ? "Partial + Risk-Free" : status;
  return {
    id: OTAS_PREFIX + String(r.trade_id),
    displayId: `Intraday · ${String(r.entry_time ?? "").slice(0, 10)}`,
    asset: "XAUUSD",
    side: Number(r.direction) < 0 ? "Sell" : "Buy",
    result,
    pips: Math.round(usd * 100),
    entry: n(r.entry_quote) != null ? Number(Number(r.entry_quote).toFixed(2)) : null,
    sl: n(st.stopPrice),
    tp1: n(st.tp1Price),
    tp2: n(st.tp2Price),
    grade: r.grade === "A_PLUS" ? "A+" : ((r.grade as string) ?? null),
    source: "Orion Live Desk",
    timeframe: "M5",
    openedAt: (r.entry_time as string) ?? null,
    closedAt: closed,
    note: null,
  };
}

async function readOtasClosed(admin: any, id?: string): Promise<CaseStudySummary[]> {
  let q = admin
    .from("otas_trades")
    .select("trade_id, status, direction, grade, entry_quote, entry_time, closed_at, state")
    .not("closed_at", "is", null);
  if (id) q = q.eq("trade_id", id);
  const { data } = await q.order("closed_at", { ascending: false }).limit(400);
  const intraday = ((data ?? []) as Record<string, unknown>[])
    .map(otasToSummary)
    .filter((t): t is CaseStudySummary => t != null);
  if (id) return intraday;
  return [...intraday, ...(await readSwingClosed(admin))];
}

const SWING_PREFIX = "osas-";


// Closed swing positions come from the latest recorded swing snapshot. The
// original stop is entry +/- stopDistance (the open snapshot shows break even
// after TP1), and the published targets are read from the snapshot that last
// carried the position open.
async function readSwingClosed(admin: any, id?: string): Promise<CaseStudySummary[]> {
  const { data: snap } = await admin
    .from("otas_swing_shadow_snapshots").select("trades").order("created_at", { ascending: false }).limit(1).maybeSingle();
  type T = { openedAt: number; closedAt: number; direction: "long" | "short"; entry: number; stopDistance: number; outcome: "TP2" | "BE" | "SL"; r: number };
  let trades = ((snap?.trades ?? []) as T[]).filter((t) => Number.isFinite(t.openedAt) && Number.isFinite(t.closedAt));
  if (id) trades = trades.filter((t) => String(t.openedAt) === id);
  return Promise.all(trades.map(async (t) => {
    const { data: o } = await admin
      .from("otas_swing_shadow_snapshots").select("summary")
      .eq("summary->open->>openedAt", String(t.openedAt))
      .order("created_at", { ascending: false }).limit(1).maybeSingle();
    const open = (o?.summary as { open?: { tp1?: number; tp2?: number } } | null)?.open;
    const side = t.direction === "long" ? 1 : -1;
    const stop = swingTradedStop(t.entry, t.stopDistance, open?.tp1);
    const usd = t.r * stop;
    const r2 = (n: number | undefined | null) => (n == null || !Number.isFinite(Number(n)) ? null : Number(Number(n).toFixed(2)));
    return {
      id: SWING_PREFIX + String(t.openedAt),
      displayId: `Swing · ${new Date(t.openedAt * 1000).toISOString().slice(0, 10)}`,
      asset: "XAUUSD",
      side: side === 1 ? "Buy" : "Sell",
      result: t.outcome === "TP2" ? "Target Achieved" : t.outcome === "SL" ? "Risk Invalidation" : usd > 0 ? "Partial + Risk-Free" : "Break Even",
      pips: Math.round(usd * 100),
      entry: r2(t.entry),
      sl: r2(t.entry - side * stop),
      tp1: r2(open?.tp1),
      tp2: r2(open?.tp2),
      grade: "A+",
      source: "Orion Live Desk",
      timeframe: "H4",
      openedAt: new Date(t.openedAt * 1000).toISOString(),
      closedAt: new Date(t.closedAt * 1000).toISOString(),
      note: null,
    } satisfies CaseStudySummary;
  }));
}

const ARCHIVE_PREFIX = "arc-";

type ArchiveRow = (typeof archiveRows)[number];

function archiveToSummary(a: ArchiveRow): CaseStudySummary {
  const usd = a.r * 20;
  return {
    id: ARCHIVE_PREFIX + a.id,
    displayId: `${a.id} · Verified archive`,
    asset: "XAUUSD",
    side: a.side === "SHORT" ? "Sell" : "Buy",
    result: a.outcome === "TARGET" ? "Target Achieved" : a.outcome === "RISK_FREE" ? "Partial + Risk-Free" : "Risk Invalidation",
    pips: Math.round(usd * 100),
    entry: a.entry,
    sl: a.sl,
    tp1: a.tp1,
    tp2: a.tp2,
    grade: a.tier === "ALPHA" ? "A+" : "A",
    source: "Verified Archive",
    timeframe: "M5",
    openedAt: new Date(a.entryTime * 1000).toISOString(),
    closedAt: new Date(a.closedAt * 1000).toISOString(),
    note: null,
  };
}

type CaseStudyList = { months: CaseStudyMonth[]; total: number };

// Live desk heartbeat, read separately so frozen years can be cached forever
// while the "last M5 scan" strip stays current.
export const getLiveDeskLastBarFn = createServerFn({ method: "GET" }).handler(async (): Promise<number | null> => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("otas_m5_bars")
    .select("t")
    .order("t", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data ? Number(data.t) : null;
});

// Per-isolate memo. Closed years are frozen, the running year is kept briefly
// so repeated opens skip the live desk reads.
const listMemo = new Map<number, { at: number; value: CaseStudyList }>();
const LIVE_MEMO_MS = 60_000;
// Tapes of live trades from finished months, frozen per isolate.
const tapeMemo = new Map<string, { t: number[]; b: number }>();

export const listCaseStudiesFn = createServerFn({ method: "GET" })
  .inputValidator((d: { year?: number } | undefined) =>
    z.object({ year: z.number().int().min(2024).max(2100).optional() }).parse(d ?? {}),
  )
  .handler(async ({ data }): Promise<CaseStudyList> => {
  const currentYear = new Date().getUTCFullYear();
  const year = data.year ?? currentYear;
  const hit = listMemo.get(year);
  if (hit && (year < 2026 || Date.now() - hit.at < LIVE_MEMO_MS)) return hit.value;

  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { default: archiveTapes } = (await import("@/data/case-study-archive-tapes.json")) as {
    default: Record<string, { b: number; t: number[] }>;
  };
  const { downsampleTape, isClosedMonth } = await import("@/lib/case-study-tape");
  // One year per request: verified archive for that year, plus live desk
  // decisions entered in that year.
  const yearOf = (iso: string | null) => (iso ? new Date(iso).getUTCFullYear() : null);
  const live = year >= 2026
    ? (await readOtasClosed(supabaseAdmin)).filter((t) => yearOf(t.openedAt ?? t.closedAt) === year)
    : [];
  const archive = archiveRows
    .filter((a) => new Date(a.entryTime * 1000).getUTCFullYear() === year)
    .map(archiveToSummary);
  // Archive tapes are precomputed (frozen history). Only live desk trades
  // read their M5 window from the database.
  const trades: CaseStudyCard[] = await Promise.all(
    [...live, ...archive]
      .sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime())
      .map(async (t) => {
        const netR = Number(riskR(t).toFixed(2));
        if (t.id.startsWith(ARCHIVE_PREFIX)) {
          const pre = archiveTapes[t.id.slice(ARCHIVE_PREFIX.length)];
          if (pre) return { ...t, tape: pre.t, bars: pre.b, netR };
        }
        const frozen = tapeMemo.get(t.id);
        if (frozen) return { ...t, tape: frozen.t, bars: frozen.b, netR };
        const fromS = Math.floor(new Date(t.openedAt ?? t.closedAt).getTime() / 1000);
        const toS = Math.floor(new Date(t.closedAt).getTime() / 1000);
        const table = t.id.startsWith(ARCHIVE_PREFIX) ? "playarea_m5_bars" : "otas_m5_bars";
        // Multi-day swing positions span thousands of M5 bars: page through
        // the full window so the sparkline reaches the exit.
        const raw: number[] = [];
        for (let page = 0; page < 10; page++) {
          const { data: rows } = await supabaseAdmin
            .from(table)
            .select("c")
            .gte("t", fromS)
            .lte("t", toS)
            .order("t", { ascending: true })
            .range(page * 1000, page * 1000 + 999);
          if (!rows?.length) break;
          for (const r of rows) raw.push(Number(r.c));
          if (rows.length < 1000) break;
        }
        const tape = downsampleTape(raw);
        // Finished months never change: keep their tape for the isolate's life.
        if (raw.length > 0 && isClosedMonth(t.closedAt)) tapeMemo.set(t.id, { t: tape, b: raw.length });
        return { ...t, tape, bars: raw.length, netR };
      }),
  );
  const months = new Map<string, CaseStudyMonth>();
  // Group by entry month, matching the published track record.
  for (const t of trades) {
    const d = new Date(t.openedAt ?? t.closedAt);
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
    let m = months.get(key);
    if (!m) {
      m = { key, label, trades: [], wins: 0, losses: 0, netR: 0 };
      months.set(key, m);
    }
    m.trades.push(t);
    m.netR += t.netR;
    if ((t.pips ?? 0) > 0) m.wins += 1;
    else if ((t.pips ?? 0) < 0) m.losses += 1;
  }

  const value: CaseStudyList = {
    months: [...months.values()].map((m) => ({ ...m, netR: Number(m.netR.toFixed(2)) })),
    total: trades.length,
  };
  listMemo.set(year, { at: Date.now(), value });
  return value;
});

export type CaseStudyDetail = {
  trade: CaseStudySummary;
  candles: { t: number; o: number; h: number; l: number; c: number }[];
  entryIndex: number;
  netR: number;
};

export const getCaseStudyFn = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ id: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<CaseStudyDetail> => loadCaseStudy(data.id));

async function loadCaseStudySummary(id: string): Promise<CaseStudySummary> {
  const data = { id };
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  if (data.id.startsWith(SWING_PREFIX)) {
    const [t] = await readSwingClosed(supabaseAdmin, data.id.slice(SWING_PREFIX.length));
    if (!t) throw new Error("Case study not found");
    return t;
  }
  if (data.id.startsWith(OTAS_PREFIX)) {
    const [t] = await readOtasClosed(supabaseAdmin, data.id.slice(OTAS_PREFIX.length));
    if (!t) throw new Error("Case study not found");
    return t;
  }
  if (data.id.startsWith(ARCHIVE_PREFIX)) {
    const row = archiveRows.find((a) => a.id === data.id.slice(ARCHIVE_PREFIX.length));
    if (!row) throw new Error("Case study not found");
    return archiveToSummary(row);
  }
  const { data: row, error } = await supabaseAdmin
    .from("trade_history")
    .select(
      "id, display_id, asset, type, timeframe, result, pips, entry, sl, tp1, tp2, source, quality_grade, generated_at, created_at, note",
    )
    .eq("id", data.id)
    .maybeSingle();
  if (error || !row) throw new Error("Case study not found");
  return toSummary(row as Record<string, unknown>);
}

export type CaseStudyMeta = { trade: CaseStudySummary; netR: number };

// Server-only listing for the sitemap, llms.txt and IndexNow: every closed
// case study with published levels only, no tape reads.
export async function listCaseStudySummariesForIndex(): Promise<CaseStudyMeta[]> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const live = await readOtasClosed(supabaseAdmin);
  return [...live, ...archiveRows.map(archiveToSummary)]
    .sort((a, b) => new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime())
    .map((trade) => ({ trade, netR: Number(riskR(trade).toFixed(2)) }));
}

// Lightweight read for page metadata: published levels only, no tape.
export const getCaseStudyMetaFn = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ id: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<(CaseStudyMeta & { cardVersion: string | null }) | null> => {
    try {
      const trade = await loadCaseStudySummary(data.id);
      let cardVersion: string | null = null
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const key = caseStudyCardKey(data.id);
        const { data: files } = await supabaseAdmin.storage.from("case-study-cards").list("", { search: key, limit: 5 });
        const hit = files?.find((f) => f.name === `${key}.jpg`);
        if (hit) cardVersion = String(Date.parse(hit.updated_at ?? hit.created_at ?? "") || 1);
      } catch {
        cardVersion = null
      }
      return { trade, netR: Number(riskR(trade).toFixed(2)), cardVersion };
    } catch {
      return null;
    }
  });

export async function loadCaseStudy(id: string): Promise<CaseStudyDetail> {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const trade = await loadCaseStudySummary(id);
    const openedS = Math.floor(new Date(trade.openedAt ?? trade.closedAt).getTime() / 1000);
    const closedS = Math.floor(new Date(trade.closedAt).getTime() / 1000);
    // Context window: about thirty M5 bars before the decision and fifteen
    // after resolution. Pages are read until the full trade is covered.
    const fromS = openedS - 150 * 60;
    const toS = closedS + 75 * 60;

    const readTape = async (table: "otas_m5_bars" | "playarea_m5_bars") => {
      const all: { t: unknown; o: unknown; h: unknown; l: unknown; c: unknown }[] = [];
      for (let page = 0; page < 20; page++) {
        const { data: rows } = await supabaseAdmin
          .from(table)
          .select("t, o, h, l, c")
          .gte("t", fromS)
          .lte("t", toS)
          .order("t", { ascending: true })
          .range(page * 1000, page * 1000 + 999);
        if (!rows || rows.length === 0) break;
        all.push(...rows);
        if (rows.length < 1000) break;
      }
      return all;
    };
    // Live tape first; archived decisions predate it, so fall back to the
    // historical research tape for the same window.
    let bars = await readTape("otas_m5_bars");
    if (bars.length === 0) bars = await readTape("playarea_m5_bars");

    // Drop synthetic placeholder bars the provider prints while the gold
    // market is closed for the weekend (Friday close to Sunday reopen), so
    // replays jump straight from the Friday close to the weekly open.
    const isWeekendFiller = (c: { t: number; h: number; l: number }) => {
      const d = new Date(c.t * 1000);
      const day = d.getUTCDay();
      const hr = d.getUTCHours();
      if (day === 6) return true;
      if (day === 5 && hr >= 22) return true;
      if (day === 0 && hr < 22) return true;
      // Boundary hours shift with daylight saving: drop them only when quiet.
      if ((day === 5 && hr === 21) || (day === 0 && hr === 22)) return c.h - c.l < 0.5;
      return false;
    };
    const candles = bars
      .map((b) => ({
        t: Number(b.t),
        o: Number(b.o),
        h: Number(b.h),
        l: Number(b.l),
        c: Number(b.c),
      }))
      .filter((c) => !isWeekendFiller(c));

    let entryIndex = candles.findIndex((c) => c.t >= openedS);
    if (entryIndex < 0) entryIndex = Math.max(0, candles.length - 1);

    return { trade, candles, entryIndex, netR: Number(riskR(trade).toFixed(2)) };
  }

async function cardExists(id: string): Promise<boolean> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const key = caseStudyCardKey(id);
  const { data: files } = await supabaseAdmin.storage.from("case-study-cards").list("", { search: key, limit: 5 });
  return !!files?.some((f) => f.name === `${key}.jpg`);
}

// Public check used by the automatic card publisher on case study pages.
export const caseStudyCardStatusFn = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ id: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }) => ({ exists: await cardExists(data.id) }));

// Automatic first publication of a trade's social card. Only accepted for a
// real closed case study with no stored card yet, JPEG bytes only, size capped.
// Admin regeneration keeps using the direct storage upsert.
// Admin only: visitors can no longer write card images to storage.
export const autoPublishCaseStudyCardFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ id: z.string().min(1).max(120), jpeg: z.string().min(100).max(700_000) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    await loadCaseStudySummary(data.id);
    if (await cardExists(data.id)) return { ok: true, created: false };
    const bin = Uint8Array.from(atob(data.jpeg), (c) => c.charCodeAt(0));
    if (bin.length > 500_000 || bin[0] !== 0xff || bin[1] !== 0xd8 || bin[2] !== 0xff) {
      throw new Error("Invalid card image");
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.storage
      .from("case-study-cards")
      .upload(`${caseStudyCardKey(data.id)}.jpg`, bin, { upsert: false, contentType: "image/jpeg" });
    return { ok: !error, created: !error };
  });
